"use strict";
/**
 * SAT Catalog Constants for CFDI Income Invoices (Facturas de Ingreso)
 *
 * Based on SAT catalogs for electronic invoicing (CFDI 4.0).
 * These are specific to income invoices — payroll catalogs are in sat-catalogs.ts.
 *
 * @see SAT Catalogs: http://omawww.sat.gob.mx/tramitesyservicios/Paginas/anexo_20_version3-3.htm
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_SAT_PAYMENT_FORM = exports.QVET_PAYMENT_METHOD_TO_SAT = exports.SAT_PAYMENT_FORM_LABELS = exports.SATPaymentForm = exports.DEFAULT_CFDI_USE = exports.SAT_CFDI_USES = exports.SAT_CLIENT_FISCAL_REGIMES_ARRAY = exports.SAT_CLIENT_FISCAL_REGIME_LABELS = exports.SATClientFiscalRegime = void 0;
exports.getCfdiUse = getCfdiUse;
exports.formatCfdiUse = formatCfdiUse;
exports.isMoralRfc = isMoralRfc;
exports.getCfdiUsesForRegime = getCfdiUsesForRegime;
exports.isCfdiUseValidForRegime = isCfdiUseValidForRegime;
exports.getCfdiUseOptions = getCfdiUseOptions;
exports.resolvePaymentForm = resolvePaymentForm;
// ============================================================================
// Fiscal Regime - Client (c_RegimenFiscal)
// ============================================================================
/**
 * SAT Fiscal Regime codes applicable to invoice receivers (clients).
 * Broader set than payroll (which only uses 605/616).
 */
exports.SATClientFiscalRegime = {
    GeneralLaw: "601",
    LegalPersonsNonProfit: "603",
    SalariesAndWages: "605",
    LeaseIncome: "606",
    BusinessActivityIncome: "612",
    WithoutFiscalObligations: "616",
    TaxIncorporation: "621", // RIF (being phased out)
    SimplifiedTrust: "625", // RESICO Personas Físicas
    SimplifiedTrustLegal: "626", // RESICO Personas Morales
};
/**
 * Fiscal regime labels in Spanish for client portal dropdowns
 */
exports.SAT_CLIENT_FISCAL_REGIME_LABELS = {
    "601": "General de Ley Personas Morales",
    "603": "Personas Morales con Fines no Lucrativos",
    "605": "Sueldos y Salarios e Ingresos Asimilados a Salarios",
    "606": "Arrendamiento",
    "612": "Personas Físicas con Actividades Empresariales y Profesionales",
    "616": "Sin obligaciones fiscales",
    "621": "Incorporación Fiscal",
    "625": "Régimen Simplificado de Confianza (Persona Física)",
    "626": "Régimen Simplificado de Confianza (Persona Moral)",
};
/**
 * Fiscal regimes array for dropdowns
 */
exports.SAT_CLIENT_FISCAL_REGIMES_ARRAY = Object.entries(exports.SAT_CLIENT_FISCAL_REGIME_LABELS).map(([code, label]) => ({
    code: code,
    label: `${code} - ${label}`,
}));
/** Regimes every key of a given family accepts — kept as consts to avoid repetition. */
const REGIMES_ALL = [
    "601", "603", "605", "606", "607", "608", "610", "611", "612",
    "614", "615", "616", "620", "621", "622", "623", "624", "625", "626",
];
/** Personal deductions (D01–D10): personas físicas, and only these regimes. */
const REGIMES_DEDUCTION = [
    "605", "606", "607", "608", "611", "612", "614", "615", "625",
];
/** Acquisitions and investments (G01/G03, I01–I08). */
const REGIMES_BUSINESS = [
    "601", "603", "606", "612", "620", "621", "622", "623", "624", "625", "626",
];
/** G02 additionally accepts 616 (sin obligaciones fiscales). */
const REGIMES_RETURNS = [...REGIMES_BUSINESS, "616"];
/**
 * The full c_UsoCFDI catalog for CFDI 4.0 — 24 keys.
 *
 * P01 ("Por definir") is deliberately absent: it was removed in CFDI 4.0 and the
 * PAC answers "El uso del cfdi P01 no existe". Facturama's own catalog endpoint
 * still returns it, so do not re-seed this list from there without filtering.
 *
 * The regime matrix was measured against the Facturama sandbox on 2026-09-20
 * (504 probes, every regime × every key). Re-measure with
 * `hvp-backend/scripts/facturama-uso-cfdi/matriz-uso-regimen.js` if it drifts.
 */
exports.SAT_CFDI_USES = [
    { code: "G01", label: "Adquisición de mercancías", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "G02", label: "Devoluciones, descuentos o bonificaciones", natural: true, moral: true, regimes: REGIMES_RETURNS },
    { code: "G03", label: "Gastos en general", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "I01", label: "Construcciones", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "I02", label: "Mobiliario y equipo de oficina por inversiones", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "I03", label: "Equipo de transporte", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "I04", label: "Equipo de cómputo y accesorios", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "I05", label: "Dados, troqueles, moldes, matrices y herramental", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "I06", label: "Comunicaciones telefónicas", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "I07", label: "Comunicaciones satelitales", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "I08", label: "Otra maquinaria y equipo", natural: true, moral: true, regimes: REGIMES_BUSINESS },
    { code: "D01", label: "Honorarios médicos, dentales y gastos hospitalarios", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D02", label: "Gastos médicos por incapacidad o discapacidad", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D03", label: "Gastos funerales", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D04", label: "Donativos", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D05", label: "Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D06", label: "Aportaciones voluntarias al SAR", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D07", label: "Primas por seguros de gastos médicos", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D08", label: "Gastos de transportación escolar obligatoria", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D09", label: "Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "D10", label: "Pagos por servicios educativos (colegiaturas)", natural: true, moral: false, regimes: REGIMES_DEDUCTION },
    { code: "S01", label: "Sin efectos fiscales", natural: true, moral: true, regimes: REGIMES_ALL },
    { code: "CP01", label: "Pagos", natural: true, moral: true, regimes: REGIMES_ALL },
    { code: "CN01", label: "Nómina", natural: true, moral: false, regimes: ["605"] },
];
/** Default when nothing else is known — the one every business regime accepts. */
exports.DEFAULT_CFDI_USE = "G03";
const CFDI_USE_BY_CODE = exports.SAT_CFDI_USES.reduce((acc, use) => {
    acc[use.code] = use;
    return acc;
}, {});
/** Look a key up. Returns undefined for anything outside the catalog (e.g. P01). */
function getCfdiUse(code) {
    return CFDI_USE_BY_CODE[code?.trim().toUpperCase()];
}
/** "G03 - Gastos en general", for dropdowns and read-only display. */
function formatCfdiUse(code) {
    const use = getCfdiUse(code);
    return use ? `${use.code} - ${use.label}` : code;
}
/**
 * Whether an RFC belongs to a persona moral. SAT RFCs are 12 positions for
 * morales and 13 for físicas; anything else is treated as unknown (undefined),
 * so callers do not silently filter on a typo.
 */
function isMoralRfc(rfc) {
    const clean = rfc?.trim().toUpperCase() ?? "";
    if (clean.length === 12)
        return true;
    if (clean.length === 13)
        return false;
    return undefined;
}
/**
 * The keys a given receiver may use.
 *
 * Filters by the fiscal regime matrix and, when the RFC is well-formed, by
 * persona física/moral. An unknown regime returns an empty list on purpose: a
 * regime we have not measured must not be allowed to offer keys the PAC rejects.
 */
function getCfdiUsesForRegime(fiscalRegime, rfc) {
    const regime = fiscalRegime?.trim();
    const moral = isMoralRfc(rfc);
    return exports.SAT_CFDI_USES.filter((use) => {
        if (!use.regimes.includes(regime))
            return false;
        if (moral === true)
            return use.moral;
        if (moral === false)
            return use.natural;
        return true;
    });
}
/** Same rule as `getCfdiUsesForRegime`, for one key — used for backend validation. */
function isCfdiUseValidForRegime(code, fiscalRegime, rfc) {
    const use = getCfdiUse(code);
    if (!use)
        return false;
    return getCfdiUsesForRegime(fiscalRegime, rfc).some((u) => u.code === use.code);
}
/**
 * Dropdown options for a receiver, as `{ code, label }` with the label already
 * prefixed by the key.
 */
function getCfdiUseOptions(fiscalRegime, rfc) {
    return getCfdiUsesForRegime(fiscalRegime, rfc).map((use) => ({
        code: use.code,
        label: `${use.code} - ${use.label}`,
    }));
}
// ============================================================================
// Payment Form (c_FormaPago)
// ============================================================================
/**
 * SAT Payment Form codes.
 * Maps from QVET payment methods to SAT-compliant codes.
 */
exports.SATPaymentForm = {
    Cash: "01",
    NominalCheck: "02",
    ElectronicTransfer: "03",
    CreditCard: "04",
    ElectronicWallet: "05",
    ElectronicMoney: "06",
    DigitalVouchers: "08",
    DebitCard: "28",
    ServiceCard: "29",
    ToBeDefined: "99",
};
/**
 * Payment form labels in Spanish
 */
exports.SAT_PAYMENT_FORM_LABELS = {
    "01": "Efectivo",
    "02": "Cheque nominativo",
    "03": "Transferencia electrónica de fondos",
    "04": "Tarjeta de crédito",
    "05": "Monedero electrónico",
    "06": "Dinero electrónico",
    "08": "Vales de despensa",
    "28": "Tarjeta de débito",
    "29": "Tarjeta de servicios",
    "99": "Por definir",
};
/**
 * Maps QVET payment method strings to SAT payment form codes.
 * QVET uses Spanish descriptors (UPPERCASE from collections report); SAT uses numeric codes.
 *
 * Source: qvet_collections.paymentMethod actual values (verified from production data).
 */
exports.QVET_PAYMENT_METHOD_TO_SAT = {
    // Exact matches from QVET collections (report 11) — all uppercase
    EFECTIVO: "01",
    "TARJETA CREDITO": "04",
    "TARJETA DEBITO": "28",
    TRANSFERENCIA: "03",
    "AMERICAN EXPRESS": "04", // AmEx is credit card
    CHEQUE: "02",
    TARJETA: "04", // Generic "card" → credit card
    PUNTOS: "05", // Points → electronic wallet
    "RECIBO DOMICILIADO": "03", // Direct debit → electronic transfer
    // Ignore: RECTIFICADO, VIDEOCONSULTA, FormaPago_MigracionEntregasACuentaAVale → "99"
};
/**
 * Default SAT payment form when QVET payment method is unknown
 */
exports.DEFAULT_SAT_PAYMENT_FORM = "99";
/**
 * Resolves a QVET payment method string to SAT payment form code.
 * Falls back to "99" (Por definir) if unknown.
 */
function resolvePaymentForm(qvetPaymentMethod) {
    if (!qvetPaymentMethod)
        return exports.DEFAULT_SAT_PAYMENT_FORM;
    const normalized = qvetPaymentMethod.trim();
    // Direct match (QVET collections uses uppercase)
    if (exports.QVET_PAYMENT_METHOD_TO_SAT[normalized]) {
        return exports.QVET_PAYMENT_METHOD_TO_SAT[normalized];
    }
    // Uppercase match (in case input comes from other source)
    const upper = normalized.toUpperCase();
    if (exports.QVET_PAYMENT_METHOD_TO_SAT[upper]) {
        return exports.QVET_PAYMENT_METHOD_TO_SAT[upper];
    }
    // Case-insensitive partial match as last resort
    const lc = normalized.toLowerCase();
    if (lc.includes("efectivo"))
        return "01";
    if (lc.includes("american") || lc.includes("amex"))
        return "04";
    if (lc.includes("crédito") || lc.includes("credito"))
        return "04";
    if (lc.includes("débito") || lc.includes("debito"))
        return "28";
    if (lc.includes("transferencia"))
        return "03";
    if (lc.includes("cheque"))
        return "02";
    if (lc.includes("tarjeta"))
        return "04";
    return exports.DEFAULT_SAT_PAYMENT_FORM;
}
